import { useState } from "react";
import { CheckCircle, AlertCircle, Send, Loader } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  name: "", email: "", phone: "", company: "", designation: "",
  city: "", country: "", message: "", accepted_terms: false,
};

export default function RegistrationSection() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) { setErrorMsg("Please accept the terms and conditions to proceed."); return; }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${BACKEND_URL}/api/register`, form);
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Submission failed. Please try again.");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  if (status === "success") {
    return (
      <section id="register" data-testid="registration-section" className="section-charcoal py-24 lg:py-32 px-6">
        <div className="max-w-2xl mx-auto text-center glass-card p-14" data-testid="registration-success">
          <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-6" />
          <p className="overline-gold mb-3">Application Received</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl text-white font-medium mb-4">
            Thank You for Your Interest
          </h2>
          <div className="gold-divider w-32 mx-auto mb-6" />
          <p className="text-[#CBD5E1] text-sm font-['Outfit'] leading-relaxed">
            Your request for an invitation has been received. The organising team at <strong className="text-[#D4AF37]">Digpu News &amp; Media</strong> will review your application and contact you within 48 hours.
          </p>
          <p className="text-[#94A3B8] text-xs mt-4 font-['Outfit']">
            Please note: All registrations are subject to vetting. Acceptance is at the organisers' sole discretion.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="register" data-testid="registration-section" className="section-charcoal py-24 lg:py-32 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="overline-gold mb-3">Request Your Invitation</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl text-white font-medium">
            Secure Your Place at the Dialogue
          </h2>
          <div className="gold-divider w-48 mx-auto mt-5" />
          <p className="text-[#94A3B8] text-sm font-['Outfit'] mt-5 max-w-xl mx-auto">
            Seats are strictly limited to 50 invited delegates. All applications are subject to review. Early expressions of interest are strongly recommended.
          </p>
        </div>

        <form data-testid="registration-form" onSubmit={handleSubmit} className="glass-card p-8 lg:p-12 flex flex-col gap-8">
          {/* Row 1: Name + Email */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="form-label" htmlFor="name">Full Name *</label>
              <input id="name" data-testid="input-name" required value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="gold-input" placeholder="Your full name" />
            </div>
            <div>
              <label className="form-label" htmlFor="email">Email Address *</label>
              <input id="email" data-testid="input-email" type="email" required value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="gold-input" placeholder="your@email.com" />
            </div>
          </div>

          {/* Row 2: Phone + Company */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="form-label" htmlFor="phone">Phone Number *</label>
              <input id="phone" data-testid="input-phone" required value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="gold-input" placeholder="+91 XXXXX XXXXX" />
            </div>
            <div>
              <label className="form-label" htmlFor="company">Company / Firm Name *</label>
              <input id="company" data-testid="input-company" required value={form.company}
                onChange={(e) => set("company", e.target.value)}
                className="gold-input" placeholder="Your organisation" />
            </div>
          </div>

          {/* Row 3: Designation + City */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="form-label" htmlFor="designation">Designation / Title *</label>
              <input id="designation" data-testid="input-designation" required value={form.designation}
                onChange={(e) => set("designation", e.target.value)}
                className="gold-input" placeholder="e.g. CEO, Managing Partner, CA" />
            </div>
            <div>
              <label className="form-label" htmlFor="city">City *</label>
              <input id="city" data-testid="input-city" required value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="gold-input" placeholder="e.g. Ahmedabad" />
            </div>
          </div>

          {/* Row 4: Country + Message */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="form-label" htmlFor="country">Country *</label>
              <input id="country" data-testid="input-country" required value={form.country}
                onChange={(e) => set("country", e.target.value)}
                className="gold-input" placeholder="e.g. India" />
            </div>
            <div>
              <label className="form-label" htmlFor="message">Why Should You Be Invited?</label>
              <textarea id="message" data-testid="input-message" rows={2} value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className="gold-input resize-none"
                placeholder="Brief note on your interest or relevance (optional)" />
            </div>
          </div>

          {/* T&C */}
          <div className="flex items-start gap-3">
            <div className="relative mt-0.5">
              <input
                id="terms"
                data-testid="input-terms"
                type="checkbox"
                checked={form.accepted_terms}
                onChange={(e) => set("accepted_terms", e.target.checked)}
                className="w-4 h-4 accent-[#D4AF37] cursor-pointer"
              />
            </div>
            <label htmlFor="terms" className="text-[#94A3B8] text-xs font-['Outfit'] leading-relaxed cursor-pointer">
              I accept the <span className="text-[#D4AF37] underline cursor-pointer">Terms &amp; Conditions</span> and <span className="text-[#D4AF37] underline cursor-pointer">Privacy Policy</span> of Digpu News &amp; Media. I understand that my application is subject to vetting and acceptance is at the organiser's discretion.
            </label>
          </div>

          {/* Error message */}
          {errorMsg && (
            <div data-testid="form-error" className="flex items-center gap-3 text-red-400 text-sm font-['Outfit'] bg-red-400/10 border border-red-400/25 p-4">
              <AlertCircle size={16} />
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              type="submit"
              data-testid="submit-registration-btn"
              disabled={status === "loading"}
              className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "loading" ? (
                <><Loader size={16} className="animate-spin" /> Submitting...</>
              ) : (
                <><Send size={15} /> Submit Your Request</>
              )}
            </button>
            <p className="text-[#94A3B8] text-xs font-['Outfit']">
              You will hear from us within 48 hours.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
